import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { nomMois, iso, nbJoursDuMois, parseIso, aujourdhui, formatLong, fetchAll } from '../../utils/planning'
import '../coordination/AssistanatPlanning.css'

function nomCourt(s) {
  return s ? `${s.prenom} ${(s.nom || '').charAt(0)}.` : '?'
}

export default function AssistanatSuperviseur() {
  const { profile } = useAuth()
  const [mois, setMois] = useState(() => {
    const t = parseIso(aujourdhui())
    return { annee: t.annee, mois: t.mois }
  })
  const [annuaire, setAnnuaire] = useState({})
  const [besoins, setBesoins] = useState([])
  const [couverture, setCouverture] = useState({})
  const [positions, setPositions] = useState({})
  const [miens, setMiens] = useState({})
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)

  const listeMois = useMemo(() => {
    const t = parseIso(aujourdhui())
    const res = []
    let a = t.annee
    let m = t.mois
    for (let i = 0; i < 8; i++) {
      res.push({ annee: a, mois: m })
      m += 1
      if (m === 13) { m = 1; a += 1 }
    }
    return res
  }, [])

  const charger = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    const debut = iso(mois.annee, mois.mois, 1)
    const fin = iso(mois.annee, mois.mois, nbJoursDuMois(mois.annee, mois.mois))
    const [rAnn, rB, rC, rD, rA] = await Promise.all([
      supabase.from('superviseurs_annuaire').select('id, prenom, nom'),
      fetchAll(() => supabase.from('besoins_assistanat').select('*').gte('date', debut).lte('date', fin).order('date')),
      fetchAll(() => supabase.from('assistanat_couverture').select('date, nb_requis, nb_affectes')
        .gte('date', debut).lte('date', fin).order('date')),
      fetchAll(() => supabase.from('dispos_assistanat').select('superviseur_id, date, dispo')
        .eq('dispo', true).gte('date', debut).lte('date', fin).order('date').order('superviseur_id')),
      fetchAll(() => supabase.from('affectations').select('date, cours')
        .eq('type', 'ASSISTANAT').eq('superviseur_id', profile.id)
        .gte('date', debut).lte('date', fin).order('date')),
    ])
    if (rAnn.error || rB.error || rC.error || rD.error || rA.error) {
      setMsg({ type: 'error', text: 'Erreur lors du chargement.' })
    }
    const ann = {}
    for (const s of rAnn.data || []) ann[s.id] = s
    const cov = {}
    for (const c of rC.data) cov[c.date] = c
    const pos = {}
    for (const d of rD.data) {
      if (!pos[d.date]) pos[d.date] = []
      pos[d.date].push(d.superviseur_id)
    }
    setAnnuaire(ann)
    setBesoins(rB.data)
    setCouverture(cov)
    setPositions(pos)
    const mm = {}
    for (const a of rA.data) mm[a.date] = a.cours || ''
    setMiens(mm)
    setLoading(false)
  }, [mois, profile])

  useEffect(() => { charger() }, [charger])

  async function basculer(date) {
    if (!profile) return
    setMsg(null)
    const dejaPositionne = (positions[date] || []).includes(profile.id)
    if (dejaPositionne) {
      // La base refuse le retrait si tu es affecté ce jour-là : la requête ne supprime alors rien, sans erreur.
      const { data: supprimees, error } = await supabase.from('dispos_assistanat').delete()
        .eq('superviseur_id', profile.id).eq('date', date).select()
      if (error) { setMsg({ type: 'error', text: 'Impossible de retirer ta disponibilité.' }); return }
      if (!supprimees || supprimees.length === 0) {
        setMsg({ type: 'error', text: "Tu es affecté(e) à cette date : contacte la coordination pour te retirer." })
        charger()
        return
      }
      setPositions(prev => ({ ...prev, [date]: (prev[date] || []).filter(id => id !== profile.id) }))
      return
    }
    const { error } = await supabase.from('dispos_assistanat')
      .upsert({ superviseur_id: profile.id, date, dispo: true }, { onConflict: 'superviseur_id,date' })
    if (error) { setMsg({ type: 'error', text: "Impossible d'enregistrer ta disponibilité." }); return }
    setPositions(prev => ({ ...prev, [date]: [...(prev[date] || []), profile.id] }))
  }

  const t = aujourdhui()

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Assistanat de cours</h2>
          <div className="page-header-sub">Positionne-toi sur les journées dont l'équipe a besoin. La coordination confirme les affectations.</div>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="as-bar">
        <select
          value={`${mois.annee}-${mois.mois}`}
          onChange={e => {
            const [a, m] = e.target.value.split('-').map(Number)
            setMois({ annee: a, mois: m })
          }}
        >
          {listeMois.map(m => (
            <option key={`${m.annee}-${m.mois}`} value={`${m.annee}-${m.mois}`}>{nomMois(m.mois)} {m.annee}</option>
          ))}
        </select>
      </div>

      {loading ? <div className="spinner" /> : besoins.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
          Aucun besoin d'assistanat saisi pour ce mois.
        </div>
      ) : (
        <div className="card as-card-sup">
          <table className="as-table as-table-sup">
            <thead>
              <tr>
                <th>Jour</th>
                <th>Besoin</th>
                <th>Déjà positionnés</th>
                <th>Ta réponse</th>
              </tr>
            </thead>
            <tbody>
              {besoins.map(b => {
                const cov = couverture[b.date]
                const affectes = cov ? cov.nb_affectes : 0
                const manque = b.nb_requis - affectes
                const ids = positions[b.date] || []
                const moi = profile ? ids.includes(profile.id) : false
                const passe = b.date < t
                return (
                  <tr key={b.date}>
                    <td className="as-date" data-label="Jour">{formatLong(b.date)}</td>
                    <td data-label="Besoin">
                      <div>{b.nb_requis} assistant(s)</div>
                      {manque > 0
                        ? <span className="badge badge-warning">Il manque {manque}</span>
                        : <span className="badge badge-actif">Complet</span>}
                      {b.commentaire && <div className="as-note">{b.commentaire}</div>}
                    </td>
                    <td data-label="Déjà positionnés">
                      {ids.length === 0 ? <span className="text-muted">Personne pour l'instant</span> : ids.map(id => (
                        <span key={id} className={`as-chip ${profile && id === profile.id ? 'as-chip-affecte' : 'as-chip-dispo'}`}>
                          {profile && id === profile.id ? 'Moi' : nomCourt(annuaire[id])}
                        </span>
                      ))}
                    </td>
                    <td data-label="Ta réponse">
                      {b.date in miens ? (
                        <>
                          <span className="badge badge-actif">Tu es affecté(e)</span>
                          {miens[b.date] && <div className="as-cours">Cours : {miens[b.date]}</div>}
                        </>
                      ) : passe ? (
                        <span className="text-muted">Passé</span>
                      ) : (
                        <button className={moi ? 'btn btn-outline btn-sm' : 'btn btn-bordeaux btn-sm'} onClick={() => basculer(b.date)}>
                          {moi ? 'Retirer ma dispo' : 'Je me positionne'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
